import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, ChevronDown, Search } from 'lucide-react';
import './FloatMenuButton.css';

/**
 * The v2 filter selector: a labelled trigger whose menu is portalled to <body> at a
 * fixed position, so it escapes any clipping/stacking context the page puts around it
 * (the Project Map's filter bar sits over a Leaflet canvas, which is what forced the
 * portal in the first place).
 *
 * Originally written inline on ProjectMapPage; lifted here so other pages get the same
 * control rather than a fourth copy. Its CSS carries its own token values, so it looks
 * identical whether it renders inside a v2 page (.pmv2) or a page with its own scoped
 * token set (.pw) — and inside the portal, where it inherits nothing at all.
 */

/**
 * Touch devices don't get autoFocus on the filter box. React's autoFocus fires in the
 * commit phase, which on iOS Safari can land outside the user-gesture window that allows
 * raising the keyboard — the keyboard then either never appears or appears and is dismissed
 * again. Letting the user tap the field costs one tap and always works.
 */
const isCoarsePointer = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;

/** Gap between trigger and menu, and the minimum margin kept against the viewport edge. */
const GAP = 6;
const EDGE = 12;
/** Option-list bounds. LIST_MAX matches `.v2-floatmenu .fm-list { max-height }` in the CSS. */
const LIST_MAX = 300;
const LIST_MIN = 120;

export interface FloatMenuOption {
  value: string;
  label: string;
  /** Optional leading swatch (Project Map uses it for location colors). */
  color?: string;
  /** Optional trailing count; when absent the row shows a checkmark for the selection. */
  count?: number;
}

export interface FloatMenuButtonProps {
  /** Small uppercase label on the trigger, e.g. "Super". */
  label: string;
  valueLabel: string;
  /** Renders the value in the muted "nothing chosen" treatment. */
  isPlaceholder: boolean;
  /** Highlights the whole trigger — i.e. this filter is narrowing the results. */
  isActive: boolean;
  options: FloatMenuOption[];
  /** Single-select: the chosen option's value. Ignored when `multiple` is set. */
  selectedValue?: string;
  /** Called with the clicked option's value. In multi-select the parent toggles it in or
   *  out of `selectedValues`; the '' option is the "All …" row and should clear the set. */
  onSelect: (value: string) => void;
  /** Multi-select: rows get a checkbox and the menu stays open while picking. The ''
   *  option (if present) reads as checked when nothing is selected, and closes the menu. */
  multiple?: boolean;
  selectedValues?: string[];
  /** Adds a type-to-filter box with arrow/enter keyboard selection. */
  searchable?: boolean;
  /** Greys out the trigger and stops it opening — e.g. the options haven't loaded. */
  disabled?: boolean;
}

const FloatMenuButton: React.FC<FloatMenuButtonProps> = ({
  label, valueLabel, isPlaceholder, isActive, options, selectedValue, onSelect, searchable, disabled,
  multiple, selectedValues,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; listMaxH: number } | null>(null);
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(0);

  useEffect(() => {
    if (open) { setQuery(''); setHi(0); }
  }, [open]);
  useEffect(() => { setHi(0); }, [query]);

  const q = query.trim().toLowerCase();
  const visible = !searchable || !q
    ? options
    : options.filter((o) => o.label.toLowerCase().includes(q));

  const isSelected = (o: FloatMenuOption) => (
    multiple
      ? (o.value === '' ? !selectedValues?.length : !!selectedValues?.includes(o.value))
      : o.value === selectedValue
  );

  // Multi-select keeps the menu (and any typed filter) open so several rows can be ticked;
  // the "All" row is a reset, so it closes like a single-select pick.
  const pick = (o: FloatMenuOption) => {
    onSelect(o.value);
    if (!multiple || o.value === '') setOpen(false);
  };

  const onSearchKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHi((h) => (visible.length ? (h + 1) % visible.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => (visible.length ? (h - 1 + visible.length) % visible.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (visible[hi]) pick(visible[hi]);
    }
  };

  // Scroll the highlighted option into view *within the option list only*. The old
  // scrollIntoView() could scroll the document instead, which on iOS is enough to shift
  // the viewport out from under the open menu.
  useEffect(() => {
    if (!open) return;
    const list = menuRef.current?.querySelector('.fm-list') as HTMLElement | null;
    const el = list?.querySelector('.fm-opt.hl') as HTMLElement | null;
    if (!list || !el) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
  }, [hi, open]);

  // Fixed position measured off the trigger, clamped into the viewport, flipping above
  // when there's more room there. Re-run on scroll/resize so the menu follows the trigger
  // rather than being torn down (see the listener effect below).
  const place = useCallback(() => {
    const t = triggerRef.current;
    if (!t) return;
    const r = t.getBoundingClientRect();
    const width = Math.max(r.width, 240);
    let left = r.left;
    if (left + width > window.innerWidth - EDGE) left = Math.max(EDGE, window.innerWidth - EDGE - width);

    // visualViewport tracks the on-screen keyboard on iOS; window.innerHeight does not.
    // Using it means the option list shrinks to the space the keyboard leaves instead of
    // sliding underneath it.
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const spaceBelow = vh - r.bottom - GAP - EDGE;
    const spaceAbove = r.top - GAP - EDGE;
    const below = spaceBelow >= spaceAbove;
    const avail = Math.max(below ? spaceBelow : spaceAbove, LIST_MIN);

    // Non-list chrome = search header + padding; measured once the menu exists.
    const menuEl = menuRef.current;
    const listEl = menuEl?.querySelector('.fm-list') as HTMLElement | null;
    const chrome = menuEl && listEl ? menuEl.offsetHeight - listEl.offsetHeight : 56;
    const listMaxH = Math.min(LIST_MAX, Math.max(LIST_MIN, Math.floor(avail) - chrome));
    const menuH = menuEl ? Math.min(menuEl.offsetHeight, chrome + listMaxH) : chrome + listMaxH;

    const top = below ? r.bottom + GAP : Math.max(EDGE, r.top - GAP - menuH);

    // Identity guard: scroll fires continuously, and a fresh object per event would
    // re-render the menu on every frame of a scroll.
    setPos((p) => (p && p.top === top && p.left === left && p.width === width && p.listMaxH === listMaxH
      ? p
      : { top, left, width, listMaxH }));
  }, []);

  // visible.length: filtering changes the menu's height, and a menu flipped above the
  // trigger is anchored by its bottom edge — without this it drifts away as the list shrinks.
  // For a menu below the trigger the result is unchanged, so the identity guard makes it free.
  useLayoutEffect(() => { if (open) place(); }, [open, place, visible.length]);

  // The menu only renders once `pos` exists, so the first place() runs with no menu to
  // measure. Re-place as the node attaches, now that its real height is known. Not a loop:
  // the callback ref is stable and only fires on attach/detach, and place() is idempotent.
  const setMenuNode = useCallback((node: HTMLDivElement | null) => {
    menuRef.current = node;
    if (node) place();
  }, [place]);

  // The menu is position:fixed against a measurement, so scroll/resize must re-anchor it.
  // It used to close instead — which on iOS Safari was self-cancelling: focusing the filter
  // box raises the keyboard, Safari scrolls to reveal the field, and that scroll unmounted
  // the field it was scrolling to.
  useEffect(() => {
    if (!open) return;
    // pointerdown, not mousedown: iOS only synthesises mouse events for elements it treats
    // as tappable, so a tap on plain background wouldn't close the menu.
    const onDocDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const vv = window.visualViewport;
    document.addEventListener('pointerdown', onDocDown);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    vv?.addEventListener('resize', place);
    vv?.addEventListener('scroll', place);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDocDown);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      vv?.removeEventListener('resize', place);
      vv?.removeEventListener('scroll', place);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place]);

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={`fsel${isActive ? ' active' : ''}`}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="fs-lab">{label}</span>
        <span className={`fs-val${isPlaceholder ? ' placeholder' : ''}`}>{valueLabel}</span>
        <ChevronDown className="caret" />
      </button>
      {open && pos && ReactDOM.createPortal(
        <div
          ref={setMenuNode}
          className="v2-floatmenu"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
        >
          {searchable && (
            <div className="fm-search">
              <Search className="ic" />
              <input
                type="text"
                placeholder="Type to filter…"
                value={query}
                autoFocus={!isCoarsePointer()}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                enterKeyHint="done"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKey}
              />
            </div>
          )}
          <div className="fm-list" style={{ maxHeight: pos.listMaxH }}>
            {visible.length === 0 ? (
              <div className="fm-empty">No matches</div>
            ) : (
              visible.map((o, i) => (
                <button
                  key={o.value}
                  type="button"
                  className={`fm-opt${isSelected(o) ? ' sel' : ''}${searchable && i === hi ? ' hl' : ''}`}
                  onClick={() => pick(o)}
                  onMouseEnter={searchable ? () => setHi(i) : undefined}
                >
                  {multiple && <span className="fm-box">{isSelected(o) && <Check />}</span>}
                  {o.color && <span className="fm-dot" style={{ background: o.color }} />}
                  <span className="fm-lab">{o.label}</span>
                  {o.count != null
                    ? <span className="fm-cnt">{o.count}</span>
                    : !multiple && <Check className="fm-check" />}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default FloatMenuButton;
